import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Mail, 
  Lock, 
  Settings as SettingsIcon, 
  RefreshCw,
  AlertTriangle,
  Target,
  BookOpen,
  History,
  Users
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/shared/Header';

interface UserPreferences {
  interests: string[];
  learning_style: string;
  progress_completed: number;
  preferred_difficulty: string;
}

const Settings = () => {
  const { user, profile } = useAuth();
  const { hasRole } = useRoleAccess();
  const [loading, setLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({
    interests: [],
    learning_style: 'mixed',
    progress_completed: 0,
    preferred_difficulty: 'beginner'
  });

  // Load user preferences from Supabase
  useEffect(() => {
    if (user?.id) {
      loadUserPreferences();
    }
  }, [user?.id]);

  const loadUserPreferences = async () => {
    if (!user?.id) return;

    try {
      setPreferencesLoading(true);

      // Get user interests from user_discipline_interests table with discipline names
      const { data: interests, error: interestsError } = await supabase
        .from('user_discipline_interests')
        .select(`
          discipline_id,
          disciplines!inner(name)
        `)
        .eq('user_id', user.id);

      if (interestsError) throw interestsError;

      // Get user progress from user_content_progress table
      const { data: progress, error: progressError } = await supabase
        .from('user_content_progress')
        .select('completion_percentage')
        .eq('user_id', user.id);

      if (progressError) throw progressError;

      // Get user preferences
      const { data: userPrefs, error: prefsError } = await supabase
        .from('user_preferences')
        .select('learning_style, preferred_difficulty')
        .eq('user_id', user.id)
        .single();

      if (prefsError && prefsError.code !== 'PGRST116') throw prefsError;

      // Calculate average progress
      const avgProgress = progress?.length > 0 
        ? Math.round(progress.reduce((sum, p) => sum + (p.completion_percentage || 0), 0) / progress.length)
        : 0;

      // Update state with real data
      setUserPreferences({
        interests: interests?.map(i => i.disciplines.name) || [],
        learning_style: userPrefs?.learning_style || 'mixed',
        progress_completed: avgProgress,
        preferred_difficulty: userPrefs?.preferred_difficulty?.toString() || 'beginner'
      });

    } catch (error) {
      console.error('Error loading user preferences:', error);
      toast.error('Failed to load your preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleResetOwnInterests = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Reset user interests
      const { error: interestsError } = await supabase
        .from('user_discipline_interests')
        .delete()
        .eq('user_id', user.id);

      if (interestsError) throw interestsError;

      // Reset user progress
      const { error: progressError } = await supabase
        .from('user_content_progress')
        .delete()
        .eq('user_id', user.id);

      if (progressError) throw progressError;

      // Reset learning preferences
      const { error: prefsError } = await supabase
        .from('user_preferences')
        .delete()
        .eq('user_id', user.id);

      if (prefsError) throw prefsError;

      // Update local state
      setUserPreferences({
        interests: [],
        progress_completed: 0,
        learning_style: 'mixed',
        preferred_difficulty: 'beginner'
      });
      
      toast.success('Your interests and progress have been reset successfully');
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Failed to reset your preferences');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyBadgeVariant = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'secondary';
      case 'intermediate': return 'default';
      case 'advanced': return 'destructive';
      default: return 'outline';
    }
  };

  const getStyleBadgeVariant = (style: string) => {
    switch (style) {
      case 'visual': return 'default';
      case 'reading': return 'secondary';
      case 'interactive': return 'destructive';
      case 'mixed': return 'outline';
      default: return 'outline';
    }
  };

  if (preferencesLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">Account Settings</h1>
          <p className="text-gray-600">Manage your account preferences and learning profile</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="text-black flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input 
                    id="fullName" 
                    value={profile?.full_name || ''} 
                    placeholder="Your full name"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    value={user?.email || ''} 
                    placeholder="your.email@example.com"
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge variant="outline">{profile?.role || 'reader'}</Badge>
                {hasRole('admin') && <Badge variant="destructive">Admin</Badge>}
                {hasRole('editor') && <Badge variant="default">Editor</Badge>}
                {hasRole('contributor') && <Badge variant="secondary">Contributor</Badge>}
              </div>
            </CardContent>
          </Card>

          {/* Current Learning Preferences */}
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-black">Learning Preferences</CardTitle>
                <Button variant="outline" size="sm" onClick={loadUserPreferences} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Interests */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-5 w-5 text-gray-600" />
                  <h3 className="font-medium text-black">Learning Interests</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {userPreferences.interests.length > 0 ? (
                    userPreferences.interests.map((interest, index) => (
                      <Badge key={index} variant="outline">{interest}</Badge>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No interests selected</p>
                  )}
                </div>
              </div>

              {/* Learning Style */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-5 w-5 text-gray-600" />
                  <h3 className="font-medium text-black">Learning Style</h3>
                </div>
                <div className="flex gap-2">
                  <Badge variant={getStyleBadgeVariant(userPreferences.learning_style)}>
                    {userPreferences.learning_style}
                  </Badge>
                  <Badge variant={getDifficultyBadgeVariant(userPreferences.preferred_difficulty)}>
                    {userPreferences.preferred_difficulty}
                  </Badge>
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <History className="h-5 w-5 text-gray-600" />
                  <h3 className="font-medium text-black">Learning Progress</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${userPreferences.progress_completed}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-black">{userPreferences.progress_completed}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reset Learning Profile */}
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="text-black flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Reset Learning Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Start fresh with a clean learning profile. This will reset all your interests, 
                progress, and personalized recommendations.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-800 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">This action cannot be undone</span>
                </div>
                <p className="text-yellow-700 text-sm mt-1">
                  You'll lose all progress and will need to rebuild your learning profile.
                </p>
              </div>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset My Learning Profile
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5" />
                      Reset Your Learning Profile
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to reset your entire learning profile? This action cannot be undone.
                      <br /><br />
                      This will:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Clear all selected disciplines and interests</li>
                        <li>Reset your learning progress to 0%</li>
                        <li>Remove personalized recommendations</li>
                        <li>Reset learning style to default settings</li>
                        <li>Reset onboarding status</li>
                      </ul>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetOwnInterests}
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {loading ? 'Resetting...' : 'Reset Profile'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="text-black flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline">
                Change Password
              </Button>
              <p className="text-sm text-gray-600">
                Last password change: {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'Never'}
              </p>
            </CardContent>
          </Card>

          {/* Admin Access */}
          {hasRole('admin') && (
            <Card className="bg-blue-50 border border-blue-200">
              <CardHeader>
                <CardTitle className="text-black flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Admin Access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  As an admin, you can manage other users' interests and accounts from the admin dashboard.
                </p>
                <Button asChild>
                  <a href="/admin">Go to Admin Dashboard</a>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Save Changes */}
          <div className="flex justify-end">
            <Button disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
